import React, { useMemo } from 'react';
import { format, parseISO, eachDayOfInterval, subDays } from 'date-fns';

export default function CalendarHeatmap({ checkIns }) {
  const days = useMemo(() => {
    const end = new Date();
    const start = subDays(end, 29);
    const allDays = eachDayOfInterval({ start, end });

    const checkInMap = {};
    for (const c of checkIns) {
      const key = c.checkInDate;
      if (!checkInMap[key]) {
        checkInMap[key] = { pain: null, isFlare: false, hasCheckIn: false };
      }
      checkInMap[key].hasCheckIn = true;
      if (c.painLevel != null) {
        checkInMap[key].pain = checkInMap[key].pain != null
          ? (checkInMap[key].pain + c.painLevel) / 2
          : c.painLevel;
      }
      if (c.isFlareDay) checkInMap[key].isFlare = true;
    }

    return allDays.map(d => {
      const key = format(d, 'yyyy-MM-dd');
      const data = checkInMap[key];
      return {
        date: d,
        dateStr: key,
        ...data,
      };
    });
  }, [checkIns]);

  const getStyle = (day) => {
    if (!day.hasCheckIn) return {
      bg: 'bg-white/[0.03]',
      border: 'border border-white/[0.06]',
      text: 'text-white/20',
      weight: 'font-normal',
    };
    if (day.isFlare) return {
      bg: 'bg-white',
      border: 'border-2 border-white',
      text: 'text-black',
      weight: 'font-bold',
    };
    if (day.pain == null || day.pain <= 2) return {
      bg: 'bg-white/10',
      border: 'border border-white/15',
      text: 'text-white/60',
      weight: 'font-medium',
    };
    if (day.pain <= 4) return {
      bg: 'bg-white/20',
      border: 'border border-white/25',
      text: 'text-white/70',
      weight: 'font-medium',
    };
    if (day.pain <= 6) return {
      bg: 'bg-white/35',
      border: 'border border-white/40',
      text: 'text-white/90',
      weight: 'font-semibold',
    };
    if (day.pain <= 8) return {
      bg: 'bg-white/55',
      border: 'border border-white/60',
      text: 'text-white',
      weight: 'font-semibold',
    };
    return {
      bg: 'bg-white/75',
      border: 'border border-white/80',
      text: 'text-white',
      weight: 'font-bold',
    };
  };

  return (
    <div className="glass rounded-xl p-6">
      <h3 className="text-sm font-semibold text-white mb-4">Last 30 Days</h3>

      <div className="grid grid-cols-10 gap-1.5">
        {days.map(day => {
          const style = getStyle(day);
          return (
            <div
              key={day.dateStr}
              className={`aspect-square rounded-md ${style.bg} ${style.border} relative cursor-default`}
              title={`${format(day.date, 'MMM d')}: ${day.hasCheckIn ? `Pain ${day.pain?.toFixed(1) || '-'}${day.isFlare ? ' (flare)' : ''}` : 'No check-in'}`}
            >
              <span className={`absolute inset-0 flex items-center justify-center text-[10px] ${style.text} ${style.weight}`}>
                {format(day.date, 'd')}
              </span>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-4 text-xs text-white/50">
        <span className="flex items-center gap-1.5">
          <span className="w-4 h-4 rounded bg-white/[0.03] border border-white/[0.06]" />
          No data
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-4 h-4 rounded bg-white/10 border border-white/15" />
          Low
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-4 h-4 rounded bg-white/35 border border-white/40" />
          Medium
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-4 h-4 rounded bg-white/55 border border-white/60" />
          High
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-4 h-4 rounded bg-white/75 border border-white/80" />
          Severe
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-4 h-4 rounded bg-white border-2 border-white" />
          Flare
        </span>
      </div>
    </div>
  );
}
